import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { 
  Lock, Users, ShoppingBag, Briefcase, ChevronDown, ChevronUp, LogOut,
  Download, Trash2, Search, Calendar, BarChart3, ArrowUpDown, Filter, X,
  Tag, Plus, Check
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple password protection
const ADMIN_PASSWORD = "TumaloBear1415!";

// Quiz questions for reference
const quizQuestions = [
  "What best describes how you currently work?",
  "What's your typical schedule like?",
  "What do you want your next layer of income to feel like?",
  "What's most true for you right now?",
  "How would you describe your approach to recommending things?"
];

const quizOptions = [
  ["I offer one-on-one services or treatments", "I sell packaged services or results-based sessions", "I lead a practice or brand but still see clients or patients directly"],
  ["Fully booked or close to it", "Booked with some flexibility", "Steady work but still building"],
  ["Something that runs quietly in the background", "Something that helps me serve more people without being more available", "Something I can be proud to share even when I am offline"],
  ["I am trusted, respected, and known for what I do", "People say they have never had results like this before", "I love what I do, but it only pays when I am in the room"],
  ["If I use and believe in something, I share it easily", "I do not sell, but I love connecting people to what works", "I am intentional, but if it helps people, I am all in"]
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [businessInquiries, setBusinessInquiries] = useState([]);
  const [productInquiries, setProductInquiries] = useState([]);
  const [funnelStats, setFunnelStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [showFilters, setShowFilters] = useState(false);
  const [quizStatusFilter, setQuizStatusFilter] = useState("all");
  const [statFilter, setStatFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState("");
  
  // Batch selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Tagging state
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(null);
  const [showBulkTagInput, setShowBulkTagInput] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchTags();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem("adminAuth", "true");
      setError("");
    } else {
      setError("Incorrect password");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/funnel-stats`)
      ]);
      
      setLeads(leadsRes.data);
      setFunnelStats(statsRes.data);
      
      const inquiries = inquiriesRes.data;
      setBusinessInquiries(inquiries.filter(i => i.type === 'business'));
      setProductInquiries(inquiries.filter(i => i.type === 'product'));
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      setAvailableTags(res.data.tags || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    
    try {
      if (type === "lead") {
        await axios.delete(`${API}/leads/${id}`);
        setLeads(leads.filter(l => l.id !== id));
      } else {
        await axios.delete(`${API}/inquiries/${id}`);
        if (type === "business") {
          setBusinessInquiries(businessInquiries.filter(i => i.id !== id));
        } else {
          setProductInquiries(productInquiries.filter(i => i.id !== id));
        }
      }
      const statsRes = await axios.get(`${API}/funnel-stats`);
      setFunnelStats(statsRes.data);
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      console.error("Error deleting:", err);
      alert("Failed to delete record");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} selected record(s)?`)) return;
    
    try {
      const ids = Array.from(selectedItems);
      if (activeTab === "leads") {
        await axios.post(`${API}/leads/bulk-delete`, { ids });
        setLeads(leads.filter(l => !selectedItems.has(l.id)));
      } else if (activeTab === "business") {
        await axios.post(`${API}/inquiries/bulk-delete`, { ids });
        setBusinessInquiries(businessInquiries.filter(i => !selectedItems.has(i.id)));
      } else {
        await axios.post(`${API}/inquiries/bulk-delete`, { ids });
        setProductInquiries(productInquiries.filter(i => !selectedItems.has(i.id)));
      }
      const statsRes = await axios.get(`${API}/funnel-stats`);
      setFunnelStats(statsRes.data);
      setSelectedItems(new Set());
      setSelectAll(false);
    } catch (err) {
      console.error("Error bulk deleting:", err);
      alert("Failed to delete records");
    }
  };

  const handleBulkTag = async () => {
    if (selectedItems.size === 0 || !bulkTagInput.trim()) return;
    
    try {
      const ids = Array.from(selectedItems);
      const tag = bulkTagInput.trim();
      
      if (activeTab === "leads") {
        await axios.post(`${API}/leads/bulk-tag`, { ids, tag });
        setLeads(leads.map(l => 
          selectedItems.has(l.id) 
            ? { ...l, tags: [...(l.tags || []), tag].filter((v, i, a) => a.indexOf(v) === i) }
            : l
        ));
      } else if (activeTab === "business") {
        await axios.post(`${API}/inquiries/bulk-tag`, { ids, tag });
        setBusinessInquiries(businessInquiries.map(i => 
          selectedItems.has(i.id)
            ? { ...i, tags: [...(i.tags || []), tag].filter((v, idx, a) => a.indexOf(v) === idx) }
            : i
        ));
      } else {
        await axios.post(`${API}/inquiries/bulk-tag`, { ids, tag });
        setProductInquiries(productInquiries.map(i => 
          selectedItems.has(i.id)
            ? { ...i, tags: [...(i.tags || []), tag].filter((v, idx, a) => a.indexOf(v) === idx) }
            : i
        ));
      }
      
      if (!availableTags.includes(tag)) {
        setAvailableTags([...availableTags, tag].sort());
      }
      setBulkTagInput("");
      setShowBulkTagInput(false);
      setSelectedItems(new Set());
      setSelectAll(false);
    } catch (err) {
      console.error("Error bulk tagging:", err);
      alert("Failed to add tag");
    }
  };

  const handleAddTag = async (type, id, tag) => {
    if (!tag.trim()) return;
    
    try {
      if (type === "lead") {
        await axios.post(`${API}/leads/${id}/tags`, { tag: tag.trim() });
        setLeads(leads.map(l => 
          l.id === id ? { ...l, tags: [...(l.tags || []), tag.trim()].filter((v, i, a) => a.indexOf(v) === i) } : l
        ));
      } else {
        await axios.post(`${API}/inquiries/${id}/tags`, { tag: tag.trim() });
        if (type === "business") {
          setBusinessInquiries(businessInquiries.map(i => 
            i.id === id ? { ...i, tags: [...(i.tags || []), tag.trim()].filter((v, idx, a) => a.indexOf(v) === idx) } : i
          ));
        } else {
          setProductInquiries(productInquiries.map(i => 
            i.id === id ? { ...i, tags: [...(i.tags || []), tag.trim()].filter((v, idx, a) => a.indexOf(v) === idx) } : i
          ));
        }
      }
      
      if (!availableTags.includes(tag.trim())) {
        setAvailableTags([...availableTags, tag.trim()].sort());
      }
      setNewTagInput("");
      setShowTagDropdown(null);
    } catch (err) {
      console.error("Error adding tag:", err);
    }
  };

  const handleRemoveTag = async (type, id, tag) => {
    try {
      if (type === "lead") {
        await axios.delete(`${API}/leads/${id}/tags/${encodeURIComponent(tag)}`);
        setLeads(leads.map(l => 
          l.id === id ? { ...l, tags: (l.tags || []).filter(t => t !== tag) } : l
        ));
      } else {
        await axios.delete(`${API}/inquiries/${id}/tags/${encodeURIComponent(tag)}`);
        if (type === "business") {
          setBusinessInquiries(businessInquiries.map(i => 
            i.id === id ? { ...i, tags: (i.tags || []).filter(t => t !== tag) } : i
          ));
        } else {
          setProductInquiries(productInquiries.map(i => 
            i.id === id ? { ...i, tags: (i.tags || []).filter(t => t !== tag) } : i
          ));
        }
      }
    } catch (err) {
      console.error("Error removing tag:", err);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = (items) => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(i => i.id)));
    }
    setSelectAll(!selectAll);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const handleStatClick = (statType) => {
    setStatFilter(statFilter === statType ? null : statType);
    setSelectedItems(new Set());
    setSelectAll(false);
    
    if (statType === 'business') {
      setActiveTab('business');
    } else if (statType === 'product') {
      setActiveTab('products');
    } else {
      setActiveTab('leads');
      if (statType === 'completed') {
        setQuizStatusFilter('completed');
      } else if (statType === 'dropoff') {
        setQuizStatusFilter('incomplete');
      } else {
        setQuizStatusFilter('all');
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Filter and sort leads
  const filteredLeads = useMemo(() => {
    let filtered = [...leads];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(l => 
        l.first_name?.toLowerCase().includes(term) ||
        l.email?.toLowerCase().includes(term)
      );
    }
    
    if (dateFilter.start) {
      filtered = filtered.filter(l => new Date(l.created_at) >= new Date(dateFilter.start));
    }
    if (dateFilter.end) {
      filtered = filtered.filter(l => new Date(l.created_at) <= new Date(dateFilter.end + "T23:59:59"));
    }
    
    if (quizStatusFilter === "completed") {
      filtered = filtered.filter(l => l.quiz_completed);
    } else if (quizStatusFilter === "incomplete") {
      filtered = filtered.filter(l => !l.quiz_completed);
    }
    
    if (tagFilter) {
      filtered = filtered.filter(l => (l.tags || []).includes(tagFilter));
    }
    
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      if (sortConfig.key === "created_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [leads, searchTerm, dateFilter, quizStatusFilter, sortConfig, tagFilter]);

  const filterInquiries = (inquiries) => {
    let filtered = [...inquiries];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i => 
        i.name?.toLowerCase().includes(term) ||
        i.email?.toLowerCase().includes(term) ||
        i.phone?.includes(term)
      );
    }
    
    if (dateFilter.start) {
      filtered = filtered.filter(i => new Date(i.created_at) >= new Date(dateFilter.start));
    }
    if (dateFilter.end) {
      filtered = filtered.filter(i => new Date(i.created_at) <= new Date(dateFilter.end + "T23:59:59"));
    }
    
    if (tagFilter) {
      filtered = filtered.filter(i => (i.tags || []).includes(tagFilter));
    }
    
    filtered.sort((a, b) => {
      const aVal = new Date(a.created_at);
      const bVal = new Date(b.created_at);
      return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  };

  const exportToCSV = (data, filename) => {
    if (!data.length) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => 
        headers.map(h => {
          let val = row[h];
          if (Array.isArray(val)) val = val.join("; ");
          if (typeof val === "string" && val.includes(",")) val = `"${val}"`;
          return val ?? "";
        }).join(",")
      )
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getQuizAnswerText = (questionIndex, answerIndex) => {
    if (quizOptions[questionIndex] && quizOptions[questionIndex][answerIndex]) {
      return quizOptions[questionIndex][answerIndex];
    }
    return `Option ${answerIndex + 1}`;
  };

  // Tag dropdown component
  const TagDropdown = ({ itemId, itemType, currentTags = [] }) => (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowTagDropdown(showTagDropdown === itemId ? null : itemId);
        }}
        className="flex items-center gap-1 text-charcoal-muted hover:text-charcoal font-inter text-xs px-2 py-1 border border-dashed border-slate-highlight/50 rounded hover:border-charcoal/30"
      >
        <Tag className="w-3 h-3" />
        Add Tag
      </button>
      
      {showTagDropdown === itemId && (
        <div className="absolute z-20 mt-1 w-48 bg-white border border-slate-highlight/30 rounded shadow-lg p-2" onClick={e => e.stopPropagation()}>
          <input
            type="text"
            placeholder="New tag name..."
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddTag(itemType, itemId, newTagInput);
              }
            }}
            className="w-full px-2 py-1 text-sm border border-slate-highlight/50 rounded mb-2"
            autoFocus
          />
          {availableTags.length > 0 && (
            <div className="max-h-32 overflow-y-auto">
              <p className="text-xs text-charcoal-muted mb-1">Existing tags:</p>
              {availableTags.filter(t => !currentTags.includes(t)).map(tag => (
                <button
                  key={tag}
                  onClick={() => handleAddTag(itemType, itemId, tag)}
                  className="w-full text-left px-2 py-1 text-sm hover:bg-bone rounded"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render tags for an item
  const renderTags = (tags = [], itemId, itemType) => (
    <div className="flex flex-wrap gap-1 items-center">
      {tags.map(tag => (
        <span key={tag} className="inline-flex items-center gap-1 bg-slate-dusty/20 text-charcoal px-2 py-0.5 rounded text-xs">
          {tag}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTag(itemType, itemId, tag);
            }}
            className="hover:text-red-600"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <TagDropdown itemId={itemId} itemType={itemType} currentTags={tags} />
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center px-6">
        <motion.div 
          className="w-full max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-bone-paper rounded-lg shadow-lg p-8 border border-slate-highlight/30">
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 bg-charcoal rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-bone" />
              </div>
            </div>
            
            <h1 className="font-playfair text-2xl text-charcoal text-center mb-6">
              Admin Access
            </h1>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-underline font-inter text-charcoal text-center"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
              
              {error && (
                <p className="text-red-600 text-sm text-center">{error}</p>
              )}
              
              <button type="submit" className="w-full btn-primary" data-testid="admin-login">
                Access Dashboard
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const currentItems = activeTab === 'leads' ? filteredLeads : 
                       activeTab === 'business' ? filterInquiries(businessInquiries) : 
                       filterInquiries(productInquiries);

  return (
    <div className="min-h-screen bg-bone">
      {/* Header */}
      <div className="bg-charcoal text-bone px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="font-playfair text-xl">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-bone/70 hover:text-bone transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-inter text-sm">Logout</span>
          </button>
        </div>
      </div>
      
      {/* Funnel Stats */}
      {funnelStats && (
        <div className="bg-bone-paper border-b border-slate-highlight/30 px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-charcoal" />
              <h2 className="font-playfair text-lg text-charcoal">Funnel Analytics</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: "Landing Signups", value: funnelStats.funnel.landing_signups, color: "bg-slate-dusty", type: "all" },
                { label: "Quiz Completed", value: funnelStats.funnel.quiz_completed, color: "bg-charcoal", type: "completed" },
                { label: "Quiz Drop-offs", value: funnelStats.funnel.quiz_drop_off, color: "bg-red-400", type: "dropoff" },
                { label: "Business Leads", value: funnelStats.funnel.business_inquiries, color: "bg-green-500", type: "business" },
                { label: "Product Leads", value: funnelStats.funnel.product_inquiries, color: "bg-blue-500", type: "product" },
              ].map((stat, i) => (
                <button
                  key={i}
                  onClick={() => handleStatClick(stat.type)}
                  className={`bg-bone rounded p-4 border transition-all text-left hover:shadow-md ${
                    statFilter === stat.type 
                      ? 'border-charcoal ring-2 ring-charcoal/20' 
                      : 'border-slate-highlight/30 hover:border-charcoal/50'
                  }`}
                >
                  <p className="font-inter text-xs text-charcoal-muted uppercase tracking-wide mb-1">{stat.label}</p>
                  <p className="font-playfair text-2xl text-charcoal">{stat.value}</p>
                  <div className={`h-1 ${stat.color} rounded mt-2`} style={{ width: `${Math.min(100, (stat.value / Math.max(1, funnelStats.funnel.landing_signups)) * 100)}%` }} />
                  <p className="font-inter text-xs text-charcoal-muted mt-2">Click to view →</p>
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Quiz Completion", value: funnelStats.conversion_rate.quiz_completion },
                { label: "To Business", value: funnelStats.conversion_rate.to_business },
                { label: "To Product", value: funnelStats.conversion_rate.to_product },
                { label: "Overall Conversion", value: funnelStats.conversion_rate.overall },
              ].map((rate, i) => (
                <div key={i} className="text-center">
                  <p className="font-inter text-xs text-charcoal-muted uppercase">{rate.label}</p>
                  <p className="font-playfair text-xl text-charcoal">{rate.value}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="border-b border-slate-highlight/30 bg-bone-paper">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'leads', label: 'Quiz Leads', icon: Users, count: leads.length },
              { id: 'business', label: 'Business Inquiries', icon: Briefcase, count: businessInquiries.length },
              { id: 'products', label: 'Product Inquiries', icon: ShoppingBag, count: productInquiries.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedItems(new Set());
                  setSelectAll(false);
                }}
                className={`flex items-center gap-2 px-4 py-3 font-inter text-sm border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-charcoal text-charcoal'
                    : 'border-transparent text-charcoal-muted hover:text-charcoal'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className="bg-charcoal/10 px-2 py-0.5 rounded text-xs">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Filters & Actions Bar */}
      <div className="bg-bone border-b border-slate-highlight/30 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-muted" />
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-highlight/50 rounded font-inter text-sm"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded font-inter text-sm ${showFilters ? 'bg-charcoal text-bone' : 'border-slate-highlight/50'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          
          <button
            onClick={() => {
              if (activeTab === "leads") exportToCSV(filteredLeads, "quiz_leads");
              else if (activeTab === "business") exportToCSV(filterInquiries(businessInquiries), "business_inquiries");
              else exportToCSV(filterInquiries(productInquiries), "product_inquiries");
            }}
            className="flex items-center gap-2 px-3 py-2 border border-slate-highlight/50 rounded font-inter text-sm hover:bg-charcoal hover:text-bone transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
        
        {showFilters && (
          <div className="max-w-7xl mx-auto mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-charcoal-muted" />
              <input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm"
              />
              <span className="text-charcoal-muted">to</span>
              <input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm"
              />
            </div>
            
            {activeTab === "leads" && (
              <select
                value={quizStatusFilter}
                onChange={(e) => setQuizStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm"
              >
                <option value="all">All Status</option>
                <option value="completed">Quiz Completed</option>
                <option value="incomplete">Quiz Incomplete</option>
              </select>
            )}
            
            {availableTags.length > 0 && (
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="px-3 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm"
              >
                <option value="">All Tags</option>
                {availableTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}
            
            <button
              onClick={() => {
                setSearchTerm("");
                setDateFilter({ start: "", end: "" });
                setQuizStatusFilter("all");
                setTagFilter("");
                setStatFilter(null);
              }}
              className="flex items-center gap-1 text-charcoal-muted hover:text-charcoal font-inter text-sm"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </button>
          </div>
        )}
      </div>
      
      {/* Bulk Actions Bar */}
      {selectedItems.size > 0 && (
        <div className="bg-charcoal/5 border-b border-slate-highlight/30 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <span className="font-inter text-sm text-charcoal">
              <strong>{selectedItems.size}</strong> item(s) selected
            </span>
            
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white rounded font-inter text-sm hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </button>
            
            {showBulkTagInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter tag name..."
                  value={bulkTagInput}
                  onChange={(e) => setBulkTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBulkTag()}
                  className="px-3 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm w-40"
                  autoFocus
                />
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      setBulkTagInput(e.target.value);
                    }
                  }}
                  className="px-2 py-1.5 border border-slate-highlight/50 rounded font-inter text-sm"
                >
                  <option value="">Existing tags...</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
                <button
                  onClick={handleBulkTag}
                  className="flex items-center gap-1 px-3 py-1.5 bg-charcoal text-white rounded font-inter text-sm"
                >
                  <Check className="w-4 h-4" />
                  Apply
                </button>
                <button
                  onClick={() => {
                    setShowBulkTagInput(false);
                    setBulkTagInput("");
                  }}
                  className="text-charcoal-muted hover:text-charcoal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowBulkTagInput(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-dusty text-white rounded font-inter text-sm hover:bg-slate-dusty/80"
              >
                <Tag className="w-4 h-4" />
                Tag Selected
              </button>
            )}
            
            <button
              onClick={() => {
                setSelectedItems(new Set());
                setSelectAll(false);
              }}
              className="text-charcoal-muted hover:text-charcoal font-inter text-sm"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-charcoal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="font-inter text-charcoal-muted">Loading data...</p>
          </div>
        ) : (
          <>
            {/* Select All + Sort Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAll && selectedItems.size === currentItems.length}
                    onChange={() => toggleSelectAll(currentItems)}
                    className="w-4 h-4 rounded border-charcoal/30"
                  />
                  <span className="font-inter text-sm text-charcoal">Select All</span>
                </label>
                <h2 className="font-playfair text-2xl text-charcoal">
                  {activeTab === 'leads' ? `Quiz Leads (${filteredLeads.length})` :
                   activeTab === 'business' ? `Business Inquiries (${filterInquiries(businessInquiries).length})` :
                   `Product Inquiries (${filterInquiries(productInquiries).length})`}
                </h2>
              </div>
              <button
                onClick={() => handleSort("created_at")}
                className="flex items-center gap-1 text-charcoal-muted hover:text-charcoal font-inter text-sm"
              >
                <ArrowUpDown className="w-4 h-4" />
                Sort by Date
              </button>
            </div>
            
            {/* Quiz Leads */}
            {activeTab === 'leads' && (
              <div className="space-y-4">
                {filteredLeads.length === 0 ? (
                  <p className="text-charcoal-muted font-inter">No leads found.</p>
                ) : (
                  filteredLeads.map((lead) => (
                    <div 
                      key={lead.id}
                      className={`bg-bone-paper rounded border overflow-hidden ${selectedItems.has(lead.id) ? 'border-charcoal ring-1 ring-charcoal/20' : 'border-slate-highlight/30'}`}
                    >
                      <div className="px-6 py-4 flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(lead.id)}
                          onChange={() => toggleSelectItem(lead.id)}
                          className="w-4 h-4 rounded border-charcoal/30 flex-shrink-0"
                          onClick={e => e.stopPropagation()}
                        />
                        <button
                          onClick={() => toggleExpand(lead.id)}
                          className="flex-1 flex items-center justify-between text-left hover:bg-bone-subtle transition-colors rounded px-2 py-1 -mx-2"
                        >
                          <div>
                            <p className="font-inter font-medium text-charcoal">{lead.first_name}</p>
                            <p className="font-inter text-sm text-charcoal-muted">{lead.email}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`px-3 py-1 rounded text-xs font-inter ${lead.quiz_completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {lead.quiz_completed ? 'Completed' : 'Incomplete'}
                            </span>
                            {lead.quiz_result && (
                              <span className="bg-charcoal/10 px-3 py-1 rounded text-xs font-inter hidden sm:inline">
                                {lead.quiz_result}
                              </span>
                            )}
                            {expandedItems[lead.id] ? (
                              <ChevronUp className="w-5 h-5 text-charcoal-muted" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-charcoal-muted" />
                            )}
                          </div>
                        </button>
                      </div>
                      
                      {expandedItems[lead.id] && (
                        <div className="px-6 py-4 border-t border-slate-highlight/30 bg-bone">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <p className="font-inter text-charcoal-muted">ID</p>
                              <p className="font-inter text-charcoal text-xs break-all">{lead.id}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Created</p>
                              <p className="font-inter text-charcoal">{formatDate(lead.created_at)}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Quiz Status</p>
                              <p className="font-inter text-charcoal">{lead.quiz_completed ? 'Completed' : 'Not Completed'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Result Type</p>
                              <p className="font-inter text-charcoal">{lead.quiz_result || 'N/A'}</p>
                            </div>
                          </div>
                          
                          {/* Tags */}
                          <div className="mb-4">
                            <p className="font-inter text-xs text-charcoal-muted uppercase tracking-wide mb-2">Tags</p>
                            {renderTags(lead.tags, lead.id, "lead")}
                          </div>
                          
                          {lead.quiz_answers && lead.quiz_answers.length > 0 && (
                            <div className="mt-4 p-4 bg-bone-paper rounded border border-slate-highlight/20">
                              <p className="font-inter text-xs text-charcoal-muted uppercase tracking-wide mb-3">Quiz Responses</p>
                              <div className="space-y-3">
                                {lead.quiz_answers.map((answer, idx) => (
                                  <div key={idx} className="text-sm">
                                    <p className="font-inter text-charcoal-muted">Q{idx + 1}: {quizQuestions[idx]}</p>
                                    <p className="font-inter text-charcoal font-medium">→ {getQuizAnswerText(idx, answer)}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => handleDelete("lead", lead.id)}
                              className="flex items-center gap-2 text-red-600 hover:text-red-700 font-inter text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Business Inquiries */}
            {activeTab === 'business' && (
              <div className="space-y-4">
                {filterInquiries(businessInquiries).length === 0 ? (
                  <p className="text-charcoal-muted font-inter">No business inquiries found.</p>
                ) : (
                  filterInquiries(businessInquiries).map((inquiry) => (
                    <div 
                      key={inquiry.id}
                      className={`bg-bone-paper rounded border overflow-hidden ${selectedItems.has(inquiry.id) ? 'border-charcoal ring-1 ring-charcoal/20' : 'border-slate-highlight/30'}`}
                    >
                      <div className="px-6 py-4 flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(inquiry.id)}
                          onChange={() => toggleSelectItem(inquiry.id)}
                          className="w-4 h-4 rounded border-charcoal/30 flex-shrink-0"
                          onClick={e => e.stopPropagation()}
                        />
                        <button
                          onClick={() => toggleExpand(inquiry.id)}
                          className="flex-1 flex items-center justify-between text-left hover:bg-bone-subtle transition-colors rounded px-2 py-1 -mx-2"
                        >
                          <div>
                            <p className="font-inter font-medium text-charcoal">{inquiry.name}</p>
                            <p className="font-inter text-sm text-charcoal-muted">{inquiry.email}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-inter text-xs text-charcoal-muted">
                              {formatDate(inquiry.created_at)}
                            </span>
                            {expandedItems[inquiry.id] ? (
                              <ChevronUp className="w-5 h-5 text-charcoal-muted" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-charcoal-muted" />
                            )}
                          </div>
                        </button>
                      </div>
                      
                      {expandedItems[inquiry.id] && (
                        <div className="px-6 py-4 border-t border-slate-highlight/30 bg-bone space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="font-inter text-charcoal-muted">Phone</p>
                              <p className="font-inter text-charcoal">{inquiry.phone || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Instagram</p>
                              <p className="font-inter text-charcoal">{inquiry.instagram || 'N/A'}</p>
                            </div>
                          </div>
                          
                          {/* Tags */}
                          <div>
                            <p className="font-inter text-xs text-charcoal-muted uppercase tracking-wide mb-2">Tags</p>
                            {renderTags(inquiry.tags, inquiry.id, "business")}
                          </div>
                          
                          <div>
                            <p className="font-inter text-charcoal-muted text-sm mb-1">About</p>
                            <p className="font-inter text-charcoal text-sm bg-bone-subtle p-3 rounded">
                              {inquiry.aboutYou}
                            </p>
                          </div>
                          
                          <div>
                            <p className="font-inter text-charcoal-muted text-sm mb-1">Hair Types</p>
                            <div className="flex flex-wrap gap-2">
                              {inquiry.hairTypes?.map((type, i) => (
                                <span key={i} className="bg-charcoal/10 px-2 py-1 rounded text-xs font-inter">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <p className="font-inter text-charcoal-muted text-sm mb-1">What excites them</p>
                            <p className="font-inter text-charcoal text-sm bg-bone-subtle p-3 rounded">
                              {inquiry.excitement}
                            </p>
                          </div>
                          
                          {inquiry.questions && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Questions</p>
                              <p className="font-inter text-charcoal text-sm bg-bone-subtle p-3 rounded">
                                {inquiry.questions}
                              </p>
                            </div>
                          )}
                          
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleDelete("business", inquiry.id)}
                              className="flex items-center gap-2 text-red-600 hover:text-red-700 font-inter text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Product Inquiries */}
            {activeTab === 'products' && (
              <div className="space-y-4">
                {filterInquiries(productInquiries).length === 0 ? (
                  <p className="text-charcoal-muted font-inter">No product inquiries found.</p>
                ) : (
                  filterInquiries(productInquiries).map((inquiry) => (
                    <div 
                      key={inquiry.id}
                      className={`bg-bone-paper rounded border overflow-hidden ${selectedItems.has(inquiry.id) ? 'border-charcoal ring-1 ring-charcoal/20' : 'border-slate-highlight/30'}`}
                    >
                      <div className="px-6 py-4 flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(inquiry.id)}
                          onChange={() => toggleSelectItem(inquiry.id)}
                          className="w-4 h-4 rounded border-charcoal/30 flex-shrink-0"
                          onClick={e => e.stopPropagation()}
                        />
                        <button
                          onClick={() => toggleExpand(inquiry.id)}
                          className="flex-1 flex items-center justify-between text-left hover:bg-bone-subtle transition-colors rounded px-2 py-1 -mx-2"
                        >
                          <div>
                            <p className="font-inter font-medium text-charcoal">{inquiry.firstName || inquiry.name}</p>
                            <p className="font-inter text-sm text-charcoal-muted">
                              {inquiry.contactMethod === 'instagram' ? `IG: ${inquiry.contact}` : inquiry.contactMethod === 'text' ? `Tel: ${inquiry.contact}` : inquiry.email}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* Language badge */}
                            {inquiry.language && (
                              <span className={`px-2 py-0.5 rounded text-xs font-inter font-medium ${
                                inquiry.language === 'ES' ? 'bg-orange-100 text-orange-700' :
                                inquiry.language === 'FR' ? 'bg-blue-100 text-blue-700' :
                                inquiry.language === 'DE' ? 'bg-amber-100 text-amber-700' :
                                inquiry.language === 'IT' ? 'bg-green-100 text-green-700' :
                                inquiry.language === 'PL' ? 'bg-red-100 text-red-700' :
                                inquiry.language === 'NL' ? 'bg-orange-100 text-orange-800' :
                                inquiry.language === 'LT' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {inquiry.language}
                              </span>
                            )}
                            <span className="font-inter text-xs text-charcoal-muted">
                              {formatDate(inquiry.created_at)}
                            </span>
                            {expandedItems[inquiry.id] ? (
                              <ChevronUp className="w-5 h-5 text-charcoal-muted" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-charcoal-muted" />
                            )}
                          </div>
                        </button>
                      </div>
                      
                      {expandedItems[inquiry.id] && (
                        <div className="px-6 py-4 border-t border-slate-highlight/30 bg-bone space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="font-inter text-charcoal-muted">Contact Method</p>
                              <p className="font-inter text-charcoal capitalize">{inquiry.contactMethod || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">{inquiry.contactMethod === 'instagram' ? 'Instagram' : 'Preferred Contact'}</p>
                              <p className="font-inter text-charcoal">{inquiry.contact || inquiry.instagram || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Phone</p>
                              <p className="font-inter text-charcoal">{inquiry.phone || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Country</p>
                              <p className="font-inter text-charcoal">{inquiry.country || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-inter text-charcoal-muted">Quiz Language</p>
                              <p className="font-inter text-charcoal">{
                                inquiry.language === 'ES' ? 'Spanish' : 
                                inquiry.language === 'EN' ? 'English' : 
                                inquiry.language === 'FR' ? 'French' :
                                inquiry.language === 'DE' ? 'German' :
                                inquiry.language === 'IT' ? 'Italian' :
                                inquiry.language === 'PL' ? 'Polish' :
                                inquiry.language === 'NL' ? 'Dutch' :
                                inquiry.language === 'LT' ? 'Lithuanian' :
                                'N/A'
                              }</p>
                            </div>
                          </div>
                          
                          {/* Tags */}
                          <div>
                            <p className="font-inter text-xs text-charcoal-muted uppercase tracking-wide mb-2">Tags</p>
                            {renderTags(inquiry.tags, inquiry.id, "product")}
                          </div>
                          
                          {/* Hair Concerns (new field) */}
                          {inquiry.hairConcerns && inquiry.hairConcerns.length > 0 && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Hair Concerns</p>
                              <div className="flex flex-wrap gap-2">
                                {inquiry.hairConcerns.map((concern, i) => (
                                  <span key={i} className="bg-charcoal/10 px-2 py-1 rounded text-xs font-inter">
                                    {concern}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Hair Routine (new field) */}
                          {inquiry.hairRoutine && inquiry.hairRoutine.length > 0 && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Hair Routine</p>
                              <div className="flex flex-wrap gap-2">
                                {inquiry.hairRoutine.map((routine, i) => (
                                  <span key={i} className="bg-slate-dusty/20 px-2 py-1 rounded text-xs font-inter">
                                    {routine}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Legacy fields for backwards compatibility */}
                          {inquiry.hairConditions && inquiry.hairConditions.length > 0 && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Hair Conditions (Legacy)</p>
                              <div className="flex flex-wrap gap-2">
                                {inquiry.hairConditions.map((condition, i) => (
                                  <span key={i} className="bg-charcoal/10 px-2 py-1 rounded text-xs font-inter">
                                    {condition}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {inquiry.hairGoals && inquiry.hairGoals.length > 0 && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Hair Goals (Legacy)</p>
                              <div className="flex flex-wrap gap-2">
                                {inquiry.hairGoals.map((goal, i) => (
                                  <span key={i} className="bg-slate-dusty/20 px-2 py-1 rounded text-xs font-inter">
                                    {goal}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {inquiry.currentProducts && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Current Products</p>
                              <p className="font-inter text-charcoal text-sm bg-bone-subtle p-3 rounded">
                                {inquiry.currentProducts}
                              </p>
                            </div>
                          )}
                          
                          {inquiry.additionalInfo && (
                            <div>
                              <p className="font-inter text-charcoal-muted text-sm mb-1">Additional Info</p>
                              <p className="font-inter text-charcoal text-sm bg-bone-subtle p-3 rounded">
                                {inquiry.additionalInfo}
                              </p>
                            </div>
                          )}
                          
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleDelete("product", inquiry.id)}
                              className="flex items-center gap-2 text-red-600 hover:text-red-700 font-inter text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
